import type {
  DeleteVodSlideMarkersMutation,
  GetWebcastSummaryManagerQuery,
  VodSlideMarker,
} from 'src/generated/graphql-manager';

import {
  Checkbox,
  IconButton,
  InputDescription,
  LinkStyleButton,
  MinusIcon,
  Modal,
  ModalPortal,
  Paragraph,
  PlusIcon,
  SaveButton,
  SecondaryButton,
  TextInput,
  Tooltip,
  TrashIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PresentationPreview } from 'src/components/presentation-preview';
import {
  useDeleteVodSlideMarkersMutation,
  useRevertVodSlideMarkersMutation,
  useUpdateVodSlideMarkersMutation,
} from 'src/generated/graphql-manager';
import { mapPresentationsToSlides } from 'src/utils/map-presentations';

import { convertToSeconds, offsetInMsToString } from './format-time';
import { validateBulkUpdate, validateSingleTimeUpdate } from './validation';

import styles from './event-summary-cue-points.module.css';

type CuePoint = {
  id: string;
  checked: boolean;
  time: string;
  error?: string;
  /** Presentation information */
  index: number;
  groupId: string;
};

const VISIBLE_CUE_POINTS = 10;
const DEFAULT_BULK_OFFSET_SHIFT = '00:00';

export function EventSummaryCuePoints({ webcast }: { webcast: GetWebcastSummaryManagerQuery['webcast'] }) {
  const { t } = useTranslation();

  const [slideMarkers, setSlideMarkers] = useState<VodSlideMarker[]>(webcast?.streaming?.vod?.slideMarkers || []);
  const [cuePoints, setCuePoints] = useState<CuePoint[]>([]);
  const [cuePointsToDelete, setCuePointsToDelete] = useState<CuePoint[]>([]);

  const [selectAllState, setSelectAllState] = useState<'all' | 'some' | 'none'>('none');
  const [bulkOffsetShift, setBulkOffsetShift] = useState(DEFAULT_BULK_OFFSET_SHIFT);
  const [bulkOffsetShiftError, setBulkOffsetShiftError] = useState<string | undefined>(undefined);
  const [hasError, setHasError] = useState<boolean>(false);
  const [viewAllSlides, setViewAllSlides] = useState<boolean>(false);
  const [revertModalOpen, setRevertModalOpen] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [previewSlide, setPreviewSlide] = useState<number | undefined>(undefined);

  const [deleteVodSlideMarkers] = useDeleteVodSlideMarkersMutation();
  const [updateSlideMarkers, { loading: updateVodSliderMarkersLoading }] = useUpdateVodSlideMarkersMutation();
  const [revertSlideMarkers] = useRevertVodSlideMarkersMutation();

  const presentations = webcast?.presentations || [];
  const primaryLanguage = webcast?.primaryLang;

  const mapDataBaseMarkers = (markers: VodSlideMarker[]) => {
    return markers.map((marker) => ({
      time: marker.offsetInMs !== undefined ? offsetInMsToString(marker.offsetInMs, true) : '',
      checked: false,
      id: marker.id,
      index: marker.index,
      groupId: marker.groupId,
    }));
  };

  useEffect(() => {
    setCuePoints(mapDataBaseMarkers(slideMarkers));
  }, [slideMarkers]);

  useEffect(() => {
    const selectedCount = cuePoints.filter((state) => state.checked === true).length;

    if (selectedCount === cuePoints.length) {
      setSelectAllState('all');
    } else if (selectedCount === 0) {
      setSelectAllState('none');
    } else {
      setSelectAllState('some');
    }
  }, [cuePoints]);

  if (!presentations) return null;

  const presentationSlides = mapPresentationsToSlides(presentations, primaryLanguage?.code);
  const pushedSlides = slideMarkers
    .map((marker) => {
      const slide = presentationSlides.find(
        (slide) => slide.index === marker.index && slide.groupId === marker.groupId
      );
      return slide
        ? {
            ...slide,
            offsetInMs: marker.offsetInMs,
            id: marker.id,
          }
        : undefined;
    })
    .filter((slide) => slide !== undefined);

  const updateCuePoints = (index: number, newValue: Partial<CuePoint>) => {
    setCuePoints((prev) => {
      const newStates = [...prev];
      newStates[index] = { ...newStates[index], ...newValue };
      return newStates;
    });
  };

  const onSelectAll = () => {
    if (selectAllState === 'all') {
      setCuePoints((prev) => prev.map((item) => ({ ...item, checked: false })));
      setSelectAllState('none');
    } else if (selectAllState === 'none') {
      setCuePoints((prev) => prev.map((item) => ({ ...item, checked: true })));
      setSelectAllState('all');
    } else {
      setCuePoints((prev) => prev.map((item) => ({ ...item, checked: false })));
      setSelectAllState('none');
    }
  };

  const onCuePointBlur = (index: number) => {
    const validationResult = validateSingleTimeUpdate(index, cuePoints, t);

    setCuePoints((prev) =>
      prev.map((cuePoint, index) => ({
        ...cuePoint,
        time: validationResult.values[index].time,
        error: validationResult.values[index].error,
      }))
    );
    setHasError(validationResult.hasError);
    setDirty(true);
  };

  const onBulkOffsetShiftBlur = (value: string) => {
    const offsetInSeconds = convertToSeconds(value, 'mm:ss', t);
    if (offsetInSeconds.error) {
      return setBulkOffsetShiftError(offsetInSeconds.error);
    }
    setBulkOffsetShiftError(undefined);
    setBulkOffsetShift(value);
  };

  const onAdjustTime = (direction: 1 | -1) => {
    const validationResult = validateBulkUpdate(cuePoints, bulkOffsetShift, direction, t);

    setCuePoints((prev) =>
      prev.map((cuePoint, index) => ({
        ...cuePoint,
        time: cuePoint.checked ? validationResult.values[index].time : cuePoint.time,
        error: validationResult.values[index].error,
        checked: false,
      }))
    );

    setHasError(validationResult.hasError);
    setBulkOffsetShift(DEFAULT_BULK_OFFSET_SHIFT);
    setDirty(true);
  };

  const handleDeleteCuePoints = () => {
    const cuePointsToDelete = cuePoints.filter((cuePoint) => cuePoint.checked);
    if (cuePointsToDelete.length === 0) return; // No cue points selected
    setCuePointsToDelete(cuePointsToDelete);
  };

  const handleDeleteCuePointsConfirm = () => {
    deleteVodSlideMarkers({
      variables: {
        input: {
          webcastId: webcast.id,
          markerIds: cuePointsToDelete.map((cuePoint) => cuePoint.id),
        },
      },
      onCompleted: (data: DeleteVodSlideMarkersMutation) => {
        const markers =
          data.deleteVodSlideMarkers.__typename === 'DeleteVodSlideMarkersSuccess'
            ? data.deleteVodSlideMarkers.markers
            : undefined;

        if (markers) {
          setCuePoints(mapDataBaseMarkers(markers));
        }
      },
    });

    setCuePointsToDelete([]);
  };

  const handleSave = () => {
    updateSlideMarkers({
      variables: {
        input: {
          webcastId: webcast.id,
          markerIds: cuePoints.map((cuePoint) => cuePoint.id),
          offsetsInMs: cuePoints.map((cuePoint) => convertToSeconds(cuePoint.time, 'hh:mm:ss').value * 1000),
        },
      },
    });
  };

  const handleRevertCuePointsConfirm = () => {
    setRevertModalOpen(false);
    revertSlideMarkers({
      variables: { webcastId: webcast.id },
      onCompleted: (data) => {
        const markers =
          data.revertVodSlideMarkers.__typename === 'RevertVodSlideMarkersSuccess'
            ? data.revertVodSlideMarkers.markers
            : undefined;

        if (markers) {
          setCuePoints(mapDataBaseMarkers(markers));
          setSlideMarkers(markers);
        }
      },
    });
  };

  const visibleCuePoints = viewAllSlides ? cuePoints : cuePoints.slice(0, VISIBLE_CUE_POINTS);

  return (
    <>
      <div className={styles.wrapper} data-testid="event-summary-cue-points">
        <div className={styles.toolbar}>
          <div className={styles.saveButtonWrapper}>
            <SecondaryButton data-testid="revert-cue-point-changes" small onClick={() => setRevertModalOpen(true)}>
              {t('manager.eventSummary.cuePoints.revertChanges')}
            </SecondaryButton>
            <SaveButton
              data-testid="save-cue-point-changes"
              small
              saving={updateVodSliderMarkersLoading}
              disabled={hasError || !dirty}
              saveLabel={t('manager.eventSummary.cuePoints.saveChanges')}
              onClick={handleSave}
            />
          </div>

          <div className={styles.selectAllWrapper}>
            <Checkbox
              data-testid="select-all-cue-points"
              onChange={onSelectAll}
              indeterminate={selectAllState === 'some'}
              checked={selectAllState === 'all'}
            >
              {t('manager.eventSummary.cuePoints.selectAll')}
            </Checkbox>

            {selectAllState !== 'none' && (
              <>
                <div data-testid="bulk-adjustment-controls" className={styles.bulkUpdateWrapper}>
                  <span>{t('manager.eventSummary.cuePoints.adjustTimeLabel')}</span>

                  <InputDescription errorMessage={bulkOffsetShiftError}>
                    <TextInput
                      data-testid="bulk-offset-shift-input"
                      className={styles.timeInput}
                      style={{ textAlign: 'center' }}
                      value={bulkOffsetShift}
                      onChange={(e) => setBulkOffsetShift(e.target.value)}
                      onBlur={(e) => onBulkOffsetShiftBlur(e.target.value)}
                    />
                  </InputDescription>

                  <LinkStyleButton
                    data-testid="adjust-cue-points-earlier-button"
                    small
                    disabled={bulkOffsetShift === DEFAULT_BULK_OFFSET_SHIFT}
                    onClick={() => onAdjustTime(-1)}
                  >
                    <MinusIcon />
                    {t('manager.eventSummary.cuePoints.adjustEarlier')}
                  </LinkStyleButton>

                  <LinkStyleButton
                    data-testid="adjust-cue-points-later-button"
                    small
                    disabled={bulkOffsetShift === DEFAULT_BULK_OFFSET_SHIFT}
                    onClick={() => onAdjustTime(1)}
                  >
                    <PlusIcon />
                    {t('manager.eventSummary.cuePoints.adjustLater')}
                  </LinkStyleButton>
                </div>

                <LinkStyleButton data-testid="delete-cue-points-button" small onClick={handleDeleteCuePoints}>
                  <TrashIcon />
                  {t('manager.eventSummary.cuePoints.deleteCuePoints')}
                </LinkStyleButton>
              </>
            )}
          </div>

          {selectAllState !== 'none' && (
            <div className={styles.selectedSlidesCount}>
              {t('manager.eventSummary.cuePoints.slidesSelected.count', {
                count: cuePoints.filter((cuePoint) => cuePoint.checked).length,
              })}
            </div>
          )}
        </div>

        <div className={styles.editCuePointsWrapper}>
          <Paragraph>{t('manager.eventSummary.cuePoints.titleBulkChange')}</Paragraph>

          <div className={styles.cuePointWrapper}>
            {visibleCuePoints.map((cuePoint, index) => {
              const slide = pushedSlides?.find(
                (slide) => slide.index === cuePoint.index && slide.groupId === cuePoint.groupId
              );

              if (!slide) return null;

              return (
                <div key={cuePoint.id} className={styles.cuePoint}>
                  <div className={styles.columnWrapper}>
                    <Checkbox
                      id={`cue-points-checkbox-${index}`}
                      data-testid={`cue-point-checkbox-${index}`}
                      checked={cuePoint.checked}
                      onChange={() => updateCuePoints(index, { checked: !cuePoint.checked })}
                    />

                    <div
                      data-testid={`slide-thumbnail-${index}`}
                      className={styles.thumbnail}
                      style={{ backgroundImage: `url('${slide.thumbnailDownloadUrl}')` }}
                    >
                      <button
                        data-testid="file-card-thumbnail-button"
                        type="button"
                        onClick={() => setPreviewSlide(index)}
                      >
                        {t('manager.eventSummary.cuePoints.viewSlide')}
                      </button>
                    </div>
                  </div>

                  <div data-testid={`slide-label-${index}`} className={styles.slideLabel}>
                    {t('manager.eventSummary.cuePoints.slideLabel', { index: index + 1 })}
                  </div>

                  <Tooltip label={t('manager.eventSummary.cuePoints.cuePointTooltip')} hidden={!cuePoint.checked}>
                    <InputDescription
                      inputId={`cue-point-input-${index}`}
                      label={t('manager.eventSummary.cuePoints.cuePointTimeLabel')}
                      className={styles.cuePointTime}
                      errorMessage={cuePoint.error}
                      disabled={cuePoint.checked}
                    >
                      <div className={styles.cuePointTimeInput}>
                        <TextInput
                          data-testid={`cue-point-input-${index}`}
                          className={styles.timeInput}
                          style={{ width: '140px' }}
                          value={cuePoint.time || ''}
                          disabled={cuePoint.checked}
                          onChange={(e) => updateCuePoints(index, { time: e.target.value })}
                          onBlur={() => onCuePointBlur(index)}
                        />
                        <Tooltip
                          label={t('manager.eventSummary.cuePoints.deleteCuePoint')}
                          hidden={cuePoint.checked}
                          position="right"
                        >
                          <IconButton
                            data-testid={`delete-cue-point-${index}`}
                            disabled={cuePoint.checked}
                            aria-label={t('manager.eventSummary.cuePoints.deleteCuePoint')}
                            onClick={() => setCuePointsToDelete([cuePoint])}
                            className={styles.deleteButton}
                            naked
                          >
                            <TrashIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </InputDescription>
                  </Tooltip>
                </div>
              );
            })}
          </div>

          {viewAllSlides === false && cuePoints.length > VISIBLE_CUE_POINTS && (
            <LinkStyleButton
              data-testid="view-all-cue-points"
              className={styles.viewAllSlidesButton}
              onClick={() => setViewAllSlides(true)}
            >
              {t('manager.eventSummary.cuePoints.viewAllCuePoints')}
            </LinkStyleButton>
          )}
        </div>
      </div>

      <ModalPortal isOpen={previewSlide !== undefined} onClose={() => setPreviewSlide(undefined)}>
        <PresentationPreview
          slides={pushedSlides.map((slide, index) => ({ ...slide, index: index + 1 }))}
          initialSlide={previewSlide}
          onClose={() => setPreviewSlide(undefined)}
          style={{ maxWidth: 1200, height: '100%', maxHeight: 830 }}
        />
      </ModalPortal>

      <Modal
        data-testid="delete-cue-points-modal"
        title={t('manager.eventSummary.cuePoints.deleteModal.title')}
        isOpen={cuePointsToDelete.length > 0}
        onClose={() => setCuePointsToDelete([])}
        footer={
          <>
            <SecondaryButton
              data-testid="cancel-delete-cue-points"
              type="button"
              onClick={() => setCuePointsToDelete([])}
            >
              {t('common.cancel')}
            </SecondaryButton>

            <SecondaryButton
              data-testid="confirm-delete-cue-points"
              type="button"
              onClick={handleDeleteCuePointsConfirm}
            >
              {t('manager.eventSummary.cuePoints.deleteModal.confirmButton.count', {
                count: cuePointsToDelete.length,
              })}
            </SecondaryButton>
          </>
        }
      >
        {t('manager.eventSummary.cuePoints.deleteModal.confirmationText.count', {
          count: cuePointsToDelete.length,
        })}
      </Modal>

      <Modal
        data-testid="revert-cue-points-modal"
        title={t('manager.eventSummary.cuePoints.revertModal.title')}
        isOpen={revertModalOpen}
        onClose={() => setRevertModalOpen(false)}
        footer={
          <>
            <SecondaryButton
              data-testid="cancel-revert-cue-points"
              type="button"
              onClick={() => setRevertModalOpen(false)}
            >
              {t('common.cancel')}
            </SecondaryButton>

            <SecondaryButton
              data-testid="confirm-revert-cue-points"
              type="button"
              onClick={handleRevertCuePointsConfirm}
            >
              {t('manager.eventSummary.cuePoints.revertModal.confirmButton')}
            </SecondaryButton>
          </>
        }
      >
        {t('manager.eventSummary.cuePoints.revertModal.confirmationText')}
      </Modal>
    </>
  );
}
